import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { AlertTriangle, ArrowRightLeft, FileUp, Plus } from 'lucide-react';
import { LopCrudPage } from '../../components/LopCrudPage';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { useFunds } from '../../hooks/useFunds';
import { getSwapUfAccounts, getSwapUfImportRows, getSwapUfLegs } from '../../api/swapUf';
import { setTenantId } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit as canEditUtil } from '../../utils/permissions';
import type { SwapUfAccount, SwapUfAction, SwapUfCreateResult, SwapUfLeg } from '../../types/SwapUf';
import { t } from '../../i18n/t';
import { SwapUfCreateModal } from './SwapUfCreateModal';
import { SwapUfImportModal } from './SwapUfImportModal';
import { SwapUfExceptionsModal } from './SwapUfExceptionsModal';
import './SwapUfPage.css';
import { formatDateOnly } from '../../utils/dateTime';

const RESOURCE = 'swapUf';

function fmtDate(p: ValueFormatterParams<SwapUfLeg>): string {
  return formatDateOnly(p.value);
}

// Percentages are stored as a decimal FRACTION (0.0005 = 0.05%); x100 is display only.
function fmtPercent(p: ValueFormatterParams<SwapUfLeg>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return `${(Number(v) * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

function fmtMoney(p: ValueFormatterParams<SwapUfLeg>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtQty(p: ValueFormatterParams<SwapUfLeg>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return Number(v).toLocaleString('he-IL');
}

function fmtRate(p: ValueFormatterParams<SwapUfLeg>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return Number(v).toLocaleString('he-IL', { maximumFractionDigits: 6 });
}

function fmtDirection(p: ValueFormatterParams<SwapUfLeg>): string {
  if (p.value === 'increase') return t('swapUf.direction.increase');
  if (p.value === 'decrease') return t('swapUf.direction.decrease');
  return '';
}

export function SwapUfPage() {
  const { user } = useAuth();
  const tenantId = user?.currentTenant?.tenantId ?? null;
  const canEdit = canEditUtil(user, RESOURCE);

  const { data: funds } = useFunds();
  const [fundId, setFundId] = useState<number | null>(null);
  const [acc, setAcc] = useState<string | null>(null);
  const [assetDraft, setAssetDraft] = useState('');
  const [assetId, setAssetId] = useState<number | null>(null);
  const [includeSettled, setIncludeSettled] = useState(false);
  const [accounts, setAccounts] = useState<SwapUfAccount[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exceptionCount, setExceptionCount] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createInit, setCreateInit] = useState<{ acc: string | null; action: SwapUfAction }>({
    acc: null,
    action: 'new',
  });
  const [importOpen, setImportOpen] = useState(false);
  const [exceptionsOpen, setExceptionsOpen] = useState(false);
  const [notice, setNotice] = useState<SwapUfCreateResult | null>(null);

  const fundOptions = useMemo(
    () => (funds ?? []).map((f) => ({ id: f.fundId, name: f.fundName })),
    [funds],
  );

  // Accounts feed the filter combo; narrowed by the chosen fund.
  useEffect(() => {
    if (tenantId === null) return;
    setTenantId(tenantId);
    let cancelled = false;
    getSwapUfAccounts(fundId)
      .then((rows) => !cancelled && setAccounts(rows))
      .catch(() => !cancelled && setAccounts([]));
    return () => {
      cancelled = true;
    };
  }, [tenantId, fundId]);

  const accOptions = useMemo(
    () => accounts.map((a) => ({ id: a.acc, name: a.fundName ?? '' })),
    [accounts],
  );

  const loadExceptionCount = useCallback(() => {
    if (tenantId === null) return;
    setTenantId(tenantId);
    getSwapUfImportRows({ status: 'exception' })
      .then((rows) => setExceptionCount(rows.length))
      .catch(() => setExceptionCount(0));
  }, [tenantId]);

  useEffect(() => {
    loadExceptionCount();
  }, [loadExceptionCount]);

  // Keyed on every filter + refreshKey: a new getRows identity makes useCrudGrid refetch.
  // setTenantId first - see useFunds / LendingsPage (hard refresh effect-ordering gotcha).
  const getRows = useCallback(async () => {
    if (tenantId !== null) setTenantId(tenantId);
    void refreshKey;
    return getSwapUfLegs({ acc, fundId, assetId, includeSettled });
  }, [tenantId, acc, fundId, assetId, includeSettled, refreshKey]);

  // Read-only screen: legs are created through the modal, never edited in the grid.
  const saveBatch = useCallback(async () => getRows(), [getRows]);

  const columnDefs = useMemo<ColDef<SwapUfLeg>[]>(
    () => [
      { field: 'acc', headerName: t('swapUf.columns.acc'), width: 110 },
      { field: 'fundAssetId', headerName: t('swapUf.columns.fundAssetId'), width: 110 },
      { field: 'fundName', headerName: t('swapUf.columns.fundName'), width: 200 },
      { field: 'assetId', headerName: t('swapUf.columns.assetId'), width: 110 },
      { field: 'assetName', headerName: t('swapUf.columns.assetName'), width: 180 },
      { field: 'swapStatusName', headerName: t('swapUf.columns.swapStatusName'), width: 130 },
      { field: 'isReset', headerName: t('swapUf.columns.isReset'), width: 90, cellDataType: 'boolean' },
      { field: 'direction', headerName: t('swapUf.columns.direction'), width: 100, cellDataType: 'text', valueFormatter: fmtDirection },
      { field: 'qty', headerName: t('swapUf.columns.qty'), width: 130, valueFormatter: fmtQty, type: 'numericColumn' },
      { field: 'idxName', headerName: t('swapUf.columns.idxName'), width: 150 },
      { field: 'dealDate', headerName: t('swapUf.columns.dealDate'), width: 115, valueFormatter: fmtDate },
      { field: 'dealIdxRate', headerName: t('swapUf.columns.dealIdxRate'), width: 130, valueFormatter: fmtRate, type: 'numericColumn' },
      { field: 'resetDate', headerName: t('swapUf.columns.resetDate'), width: 115, valueFormatter: fmtDate },
      { field: 'resetIdxRate', headerName: t('swapUf.columns.resetIdxRate'), width: 130, valueFormatter: fmtRate, type: 'numericColumn' },
      { field: 'lastIdxRate', headerName: t('swapUf.columns.lastIdxRate'), width: 130, valueFormatter: fmtRate, type: 'numericColumn' },
      { field: 'interestUsed', headerName: t('swapUf.columns.interestUsed'), width: 100, valueFormatter: fmtPercent, type: 'numericColumn' },
      { field: 'spreadPercent', headerName: t('swapUf.columns.spreadPercent'), width: 100, valueFormatter: fmtPercent, type: 'numericColumn' },
      { field: 'buyFeePercent', headerName: t('swapUf.columns.buyFeePercent'), width: 110, valueFormatter: fmtPercent, type: 'numericColumn' },
      { field: 'sellFeePercent', headerName: t('swapUf.columns.sellFeePercent'), width: 110, valueFormatter: fmtPercent, type: 'numericColumn' },
      { field: 'mtm', headerName: t('swapUf.columns.mtm'), width: 130, valueFormatter: fmtMoney, type: 'numericColumn' },
      { field: 'mgm', headerName: t('swapUf.columns.mgm'), width: 130, valueFormatter: fmtMoney, type: 'numericColumn' },
      { field: 'valuationDate', headerName: t('swapUf.columns.valuationDate'), width: 120, valueFormatter: fmtDate },
      { field: 'unknownReason', headerName: t('swapUf.columns.unknownReason'), width: 220 },
      { field: 'createdBy', headerName: t('swapUf.columns.createdBy'), width: 120 },
    ],
    [],
  );

  const openCreate = useCallback((initAcc: string | null, action: SwapUfAction) => {
    setCreateInit({ acc: initAcc, action });
    setCreateOpen(true);
  }, []);

  // Right-click on a row -> adjust that account. Memoized (the menu's items memo depends on it).
  const extraMenuItems = useCallback(
    ({ rowData }: { rowData: Record<string, unknown> | null }) => {
      const rowAcc = rowData?.acc;
      if (!canEdit || rowAcc === undefined || rowAcc === null) return [];
      return [
        {
          label: t('swapUf.contextMenu.adjust'),
          icon: <ArrowRightLeft size={16} />,
          onClick: () => openCreate(String(rowAcc), 'increase'),
        },
      ];
    },
    [canEdit, openCreate],
  );

  const commitAsset = useCallback(() => {
    const n = Number(assetDraft.trim());
    setAssetId(assetDraft.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null);
  }, [assetDraft]);

  const onCreated = useCallback((r: SwapUfCreateResult) => {
    setCreateOpen(false);
    setNotice(r);
    setRefreshKey((k) => k + 1);
  }, []);
  const onDataChanged = useCallback(() => {
    setRefreshKey((k) => k + 1);
    loadExceptionCount();
  }, [loadExceptionCount]);
  const closeCreate = useCallback(() => setCreateOpen(false), []);
  const closeImport = useCallback(() => setImportOpen(false), []);
  const closeExceptions = useCallback(() => {
    setExceptionsOpen(false);
    loadExceptionCount();
  }, [loadExceptionCount]);

  return (
    <div className="swap-uf-page">
      <div className="swap-uf-page__filter">
        <label htmlFor="swap-uf-fund-filter">{t('swapUf.filters.fund')}</label>
        <div className="swap-uf-page__combo">
          <LopSearchableSelect
            id="swap-uf-fund-filter"
            value={fundId}
            options={fundOptions}
            onChange={(v) => {
              setFundId(v);
              setAcc(null);
            }}
            allowNone
            noneLabel={t('swapUf.filters.allFunds')}
            placeholder={t('swapUf.filters.allFunds')}
          />
        </div>
        <label htmlFor="swap-uf-acc-filter">{t('swapUf.filters.acc')}</label>
        <div className="swap-uf-page__combo">
          <LopSearchableSelect
            id="swap-uf-acc-filter"
            value={acc}
            options={accOptions}
            onChange={setAcc}
            allowNone
            noneLabel={t('swapUf.filters.allAccounts')}
            placeholder={t('swapUf.filters.allAccounts')}
          />
        </div>
        <label htmlFor="swap-uf-asset-filter">{t('swapUf.filters.assetId')}</label>
        <input
          id="swap-uf-asset-filter"
          className="swap-uf-page__asset"
          type="number"
          min="0"
          value={assetDraft}
          onChange={(e) => setAssetDraft(e.target.value)}
          onBlur={commitAsset}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitAsset();
          }}
        />
        <label className="swap-uf-page__check">
          <input
            id="swap-uf-include-settled"
            type="checkbox"
            checked={includeSettled}
            onChange={(e) => setIncludeSettled(e.target.checked)}
          />
          {t('swapUf.filters.includeSettled')}
        </label>
        <span className="swap-uf-page__spacer" />
        <div className="swap-uf-page__actions">
        {canEdit && (
          <>
            <button
              type="button"
              id="swap-uf-new"
              className="swap-uf-btn swap-uf-btn--primary"
              onClick={() => openCreate(null, 'new')}
            >
              <Plus size={14} />
              {t('swapUf.actions.newSwap')}
            </button>
            <button
              type="button"
              id="swap-uf-import"
              className="swap-uf-btn"
              onClick={() => setImportOpen(true)}
            >
              <FileUp size={14} />
              {t('swapUf.actions.importFile')}
            </button>
          </>
        )}
        <button
          type="button"
          id="swap-uf-exceptions"
          className="swap-uf-btn"
          onClick={() => setExceptionsOpen(true)}
        >
          <AlertTriangle size={14} />
          {t('swapUf.actions.exceptions')}
          <span
            id="swap-uf-exceptions-count"
            className={`swap-uf-page__badge${exceptionCount > 0 ? ' has-items' : ''}`}
          >
            {exceptionCount}
          </span>
        </button>
        </div>
      </div>
      {notice && (
        <div className="swap-uf-page__notice" role="status" id="swap-uf-notice">
          <span>
            {t('swapUf.notice.created', { assetId: notice.assetId })}
            {notice.isClosing && <strong> — {t('swapUf.notice.closing')}</strong>}
          </span>
          <button type="button" className="swap-uf-btn swap-uf-btn--small" onClick={() => setNotice(null)}>
            {t('swapUf.notice.dismiss')}
          </button>
        </div>
      )}
      <LopCrudPage<SwapUfLeg>
        resourceCode={RESOURCE}
        titleKey="swapUf.pageTitle"
        idField="assetId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        showAdd={false}
        showDelete={false}
        extraMenuItems={extraMenuItems}
      />
      {createOpen && (
        <SwapUfCreateModal
          initialAction={createInit.action}
          initialAcc={createInit.acc}
          onClose={closeCreate}
          onCreated={onCreated}
        />
      )}
      {importOpen && <SwapUfImportModal onClose={closeImport} onImported={onDataChanged} />}
      {exceptionsOpen && (
        <SwapUfExceptionsModal canEdit={canEdit} onClose={closeExceptions} onChanged={onDataChanged} />
      )}
    </div>
  );
}
